import { ACTION_TYPE } from "../core/redux/actionType";
import {
  hasTrippedBreaker,
  updateBreaker,
} from "../core/redux/api/circuitBreaker/module";
import { apiUpdateEffectiveDateRangeRules } from "../core/redux/api/endpoints";
import {
  addError,
  addLoading,
  removeLoadingAndError,
} from "../core/redux/api/module";
import {
  ApiReducer,
  makeApiAction,
  makeApiReducer,
  RsaaTypes,
} from "../core/redux/api/reduxStandardApiAction";
import { IAppState } from "../core/redux/IAppState";
import { getResource } from "../language/resources/getResource";
import { LiftAccessRuleBatchSaveResult } from "../productAdministration/models";
import {
  EditableEffectiveDateRangeAccessRule,
  ValidationConfigurationChangeSet,
  ValidationConfigurationUpdateRequest,
} from "./models";

const KEY_ACTION =
  ACTION_TYPE.UPDATE_EDITABLE_EFFECTIVE_DATE_RANGE_ACCESS_RULES_REQUEST;

const ACTIONS = [
  ACTION_TYPE.UPDATE_EDITABLE_EFFECTIVE_DATE_RANGE_ACCESS_RULES_REQUEST,
  ACTION_TYPE.UPDATE_EDITABLE_EFFECTIVE_DATE_RANGE_ACCESS_RULES_RESPONSE,
  ACTION_TYPE.UPDATE_EDITABLE_EFFECTIVE_DATE_RANGE_ACCESS_RULES_ERROR,
];

/**
 * POST: Update a resort's editable effective date range access rules for
 * the Validation Configuration Tool.
 */
export const updateEditableEffectiveDateRangeRulesAction = (
  updateRequest: ValidationConfigurationUpdateRequest,
) =>
  makeApiAction({
    endpoint: apiUpdateEffectiveDateRangeRules(),
    method: "POST",
    body: JSON.stringify(updateRequest),

    headers: (state: IAppState) => ({
      "content-type": "application/json",
      Authorization: `Bearer ${state.auth.bearerToken}`,
    }),

    types: ACTIONS.map((type) => ({
      type,
      meta: { updateRequest },
    })) as RsaaTypes,

    bailout: (state: IAppState) => hasTrippedBreaker(state, KEY_ACTION),
  });

function applyEffectiveDateRangeRuleUpdates(
  current: EditableEffectiveDateRangeAccessRule[],
  updates: ValidationConfigurationChangeSet[],
): EditableEffectiveDateRangeAccessRule[] {
  const updatesById = new Map<number, ValidationConfigurationChangeSet>();

  for (const update of updates) {
    updatesById.set(update.rule.liftAccessRuleId, update);
  }

  return current.map((item) => {
    const update = updatesById.get(item.liftAccessRuleId);

    if (!update) {
      return item;
    }

    return {
      ...item,
      effectiveDate: update.rule.effectiveDateStart,
      expirationDate: update.rule.effectiveDateEnd,
    };
  });
}

const reducer: ApiReducer<LiftAccessRuleBatchSaveResult> = (
  state,
  action,
  error,
  isLoading,
  payload,
) => {
  if (isLoading) {
    return addLoading(state, KEY_ACTION, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(withBreaker, KEY_ACTION, KEY_ACTION, error);
  }

  const nextState = removeLoadingAndError(withBreaker, KEY_ACTION, KEY_ACTION);

  // The rules displayed in the UI are update all or none.   There may be failures if the children entitlement types
  // could not be updated.  Failures due to missing rules for Child Entitlement Types are logged in AppInsights 
  // but do not cause the request to fail
  if (payload && payload.successCount === 0 && payload.failureCount > 0) {
    return addError(
      nextState,
      KEY_ACTION,
      KEY_ACTION,
      getResource(
        state.locale,
        "VALIDATION_CONFIG_FAILED_NETWORK_REQUEST",
      ),
    );
  }

  const updateRequest = action.meta?.updateRequest as
    | ValidationConfigurationUpdateRequest
    | undefined;

  if (!updateRequest?.effectiveDateRangeRuleUpdates?.length) {
    return nextState;
  }

  const updatedRules = applyEffectiveDateRangeRuleUpdates(
    nextState.editableEffectiveDateRangeAccessRules,
    updateRequest?.effectiveDateRangeRuleUpdates,
  );

  return {
    ...nextState,
    editableEffectiveDateRangeAccessRules: updatedRules,
  };
};

export const updateEditableEffectiveDateRangeRulesReducer =
  makeApiReducer<LiftAccessRuleBatchSaveResult>(ACTIONS, KEY_ACTION, reducer);
